import { useMemo } from 'react'

export interface FeedbackMessage {
  id: string
  sender: string
  avatar: string | null
  type: string
  status: 'approved' | 'needs_revisions' | null
  message: string
  timestamp: string
  date: string
  milestoneName?: string
  replies: Array<{ id: string; sender: string; message: string; timestamp: string }>
}

// Format date as relative time (e.g., "2 days ago", "just now")
const formatRelativeTime = (date: Date): string => {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// Format date for feedback grouping (e.g., "Today, January 15" or "Monday, January 13")
const formatFeedbackDate = (date: Date): string => {
  const now = new Date()
  const isToday = date.toDateString() === now.toDateString()
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  const isYesterday = date.toDateString() === yesterday.toDateString()

  if (isToday) {
    return 'Today, ' + date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
  }
  if (isYesterday) {
    return 'Yesterday, ' + date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
  }
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
}

export const useFeedbackMessages = (campaign: any) => {
  return useMemo<FeedbackMessage[]>(() => {
    const messages: FeedbackMessage[] = []

    // 1. Extract feedback from timeline (user and review messages)
    if (campaign?.timeline) {
      campaign.timeline
        .filter((msg: any) => msg.messageType === 'user' || msg.messageType === 'review')
        .forEach((msg: any) => {
          const date = new Date(msg.createdAt)
          const isReview = msg.messageType === 'review'
          const action = msg.metadata?.action

          messages.push({
            id: `timeline-${msg.id}`,
            sender: msg.author?.name || campaign.brand?.name || 'Brand',
            avatar: msg.author?.avatar || campaign.brand?.logo || null,
            type: msg.milestone?.name || 'General',
            status: isReview ? (action === 'approve' ? 'approved' : 'needs_revisions') : null,
            message: msg.body,
            timestamp: formatRelativeTime(date),
            date: formatFeedbackDate(date),
            milestoneName: msg.milestone?.name,
            replies: [],
          })
        })
    }

    // 2. Extract feedback from submission reviewNotes (for approved submissions with additionalComments)
    if (campaign?.milestones) {
      campaign.milestones.forEach((milestone: any) => {
        milestone.submissions?.forEach((submission: any) => {
          if (
            submission.reviewNotes?.additionalComments &&
            submission.status === 'approved' &&
            submission.reviewedAt
          ) {
            const date = new Date(submission.reviewedAt)
            // Avoid duplicate if already extracted from timeline review message
            const isDuplicate = messages.some(
              (m) =>
                m.message.includes(submission.reviewNotes.additionalComments) &&
                m.type === milestone.name,
            )
            if (!isDuplicate) {
              messages.push({
                id: `submission-${submission.id}`,
                sender: submission.reviewer?.name || campaign.brand?.name || 'Brand',
                avatar: submission.reviewer?.avatar || campaign.brand?.logo || null,
                type: milestone.name,
                status: 'approved',
                message: submission.reviewNotes.additionalComments,
                timestamp: formatRelativeTime(date),
                date: formatFeedbackDate(date),
                milestoneName: milestone.name,
                replies: [],
              })
            }
          }
        })
      })
    }

    // Sort by most recent first
    return messages.sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    )
  }, [campaign])
}
